"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { Check, Copy, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Recipient-facing action row for the public share page.
 *
 * Renders two affordances:
 *   1. **Copy link** — writes the share URL to the clipboard
 *      via the modern Clipboard API (with a textarea-fallback
 *      for older browsers). Inline check icon for ~2 s on
 *      success so the action feels confirmed without needing
 *      to read the toast.
 *   2. **Share** — pops the OS-native share sheet on mobile
 *      (and Edge / Chrome on Windows 11+) via
 *      `navigator.share`. When unavailable, falls back to
 *      copying the link so the button is never a dead end.
 *
 * Designed to live in a thin row right above the first
 * block — visible without competing with the operator /
 * customer brand strip in the header.
 *
 * Props:
 *   - `url`   — fully-qualified share URL (the recipient's
 *               current address; we resolve `window.location`
 *               on click rather than at render time so a
 *               stale prerendered URL doesn't ship with the
 *               page).
 *   - `title` — used as the OS share-sheet title.
 *   - `text`  — optional one-liner for the share-sheet body.
 */

interface Props {
  url: string;
  title: string;
  text?: string | null;
  /**
   * Icon-only variant for compact overlay pills (e.g. Launch shell's
   * top-right cluster). Drops the text labels + borders and shrinks
   * the buttons to icon-button proportions while preserving the
   * tap-target + transition semantics. Defaults to false — the
   * full-chrome share page keeps the labelled buttons.
   */
  iconOnly?: boolean;
  /** Launch overlays opt into 44px controls without changing other compact consumers. */
  touchTarget?: boolean;
  /** Use the caller's model/exact-view link instead of the address bar. */
  preferProvidedUrl?: boolean;
  /** Show a selectable fallback if clipboard access is denied or never responds. */
  resilientClipboard?: boolean;
  /** Resolve the latest camera view synchronously within the user's share gesture. */
  getUrl?: () => string;
  /** Launch uses one Share trigger with copy and available device-sharing actions. */
  consolidated?: boolean;
}

export default function ShareActions({ url, title, text, iconOnly = false, touchTarget = false, preferProvidedUrl = false, resilientClipboard = false, getUrl, consolidated = false }: Props) {
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [nativeShareAvailable, setNativeShareAvailable] = useState(false);
  const [manualLink, setManualLink] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const generation = useRef(0);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);
  const manualInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (manualLink) manualInputRef.current?.focus(); }, [manualLink]);
  useEffect(() => { setNativeShareAvailable(typeof navigator.share === "function"); }, []);

  useEffect(() => {
    generation.current += 1;
    setCopied(false);
    setManualLink(null);
    setShareOpen(false);
    return () => {
      generation.current += 1;
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    };
  }, [url]);

  function getCanonicalUrl(): string {
    if (getUrl) return getUrl();
    if (preferProvidedUrl) return url;
    if (typeof window !== "undefined" && window.location?.href) {
      return window.location.href;
    }
    return url;
  }

  async function copyToClipboard(value: string): Promise<boolean> {
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard?.writeText
      ) {
        if (resilientClipboard) {
          let timer: ReturnType<typeof setTimeout> | undefined;
          try {
            return await Promise.race([
              navigator.clipboard.writeText(value).then(() => true, () => false),
              new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), 5000); }),
            ]);
          } finally {
            clearTimeout(timer);
          }
        }
        await navigator.clipboard.writeText(value);
        return true;
      }
    } catch {
      // Some sandboxed contexts (iframes, http://) reject the
      // Clipboard API — fall through to the textarea fallback.
    }
    if (resilientClipboard) return false;
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    return ok;
  }

  function confirmCopy() {
    setManualLink(null);
    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopied(false), 2000);
  }

  function resetCopyFeedback() {
    setCopied(false);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = null;
  }

  function handleCopy() {
    if (pending) return;
    resetCopyFeedback();
    const attempt = generation.current;
    const finalUrl = getCanonicalUrl();
    startTransition(async () => {
      const ok = await copyToClipboard(finalUrl);
      if (attempt !== generation.current) return;
      if (!ok) {
        if (resilientClipboard) { setManualLink(finalUrl); return; }
        toast.error("Couldn't copy — try selecting the URL manually.");
        return;
      }
      confirmCopy();
      toast.success("Link copied");
    });
  }

  function handleShare() {
    if (pending) return;
    resetCopyFeedback();
    const attempt = generation.current;
    const finalUrl = getCanonicalUrl();
    startTransition(async () => {
      // navigator.share is gated by `isSecureContext` + a
      // browser allowlist (mobile Safari/Chrome, desktop Edge,
      // recent Chrome on Windows). Feature-detect + fall back
      // to copy so the button does SOMETHING everywhere.
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({
            title,
            text: text ?? undefined,
            url: finalUrl,
          });
          // On success the OS handles the toast / dismissal.
          if (consolidated && attempt === generation.current) handleShareOpenChange(false);
          return;
        } catch (err) {
          if (attempt !== generation.current) return;
          // AbortError = user closed the sheet — silent. Anything
          // else falls through to the clipboard fallback.
          if ((err as { name?: string })?.name === "AbortError") return;
        }
      }
      const ok = await copyToClipboard(finalUrl);
      if (attempt !== generation.current) return;
      if (ok) {
        confirmCopy();
        toast.success("Sharing isn't available on this browser — link copied instead.");
      } else {
        if (resilientClipboard) { setManualLink(finalUrl); return; }
        toast.error("Couldn't share — try copying the URL manually.");
      }
    });
  }

  function handleShareOpenChange(open: boolean) {
    if (!open) {
      // Closing is an explicit dismissal: ignore late clipboard results instead
      // of reopening the surface or displaying a previous model's link.
      generation.current += 1;
      setManualLink(null);
    }
    setShareOpen(open);
  }

  function dismissManualLink() {
    setManualLink(null);
    if (consolidated) handleShareOpenChange(false);
    (consolidated ? shareTriggerRef : copyButtonRef).current?.focus();
  }
  const manualFallback = manualLink ? <div onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); dismissManualLink(); } }} className={consolidated ? "mt-2 border-t border-white/15 pt-3" : "absolute right-0 top-full mt-3 w-[min(20rem,calc(100vw-3rem))] rounded-xl border border-white/20 bg-black/95 p-3 text-white shadow-xl"}>
    <p role="status" className="mb-2 text-xs">Copy this link to share the view.</p>
    <input ref={manualInputRef} type="text" aria-label="Share link" value={manualLink} readOnly onFocus={(event) => event.currentTarget.select()} className="min-h-11 w-full rounded-md border border-white/30 bg-white/10 px-2 text-base sm:text-sm" />
    <button type="button" onClick={dismissManualLink} className="mt-1 min-h-11 px-2 text-xs underline focus-visible:outline-2 focus-visible:outline-white">Dismiss</button>
  </div> : null;

  if (consolidated) {
    return (
      <Popover open={shareOpen} onOpenChange={handleShareOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={shareTriggerRef}
            type="button"
            aria-label="Share"
            aria-busy={pending}
            title="Share this view"
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white focus-visible:outline-2 focus-visible:outline-white motion-reduce:transition-none touch-manipulation"
          >
            {pending ? <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" /> : copied ? <Check aria-hidden="true" className="size-4 text-emerald-400" /> : <Share2 aria-hidden="true" className="size-4" />}
            <span role="status" className="sr-only">{copied ? "Link copied" : ""}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={8}
          collisionPadding={16}
          aria-label="Share this view"
          className="[--glass-popover:rgb(0_0_0/0.9)] [--glass-edge:rgb(255_255_255/0.15)] [--card:0_0%_4%] max-h-[var(--radix-popover-content-available-height)] w-72 max-w-[calc(100vw-2rem-env(safe-area-inset-left)-env(safe-area-inset-right))] overflow-y-auto overscroll-contain rounded-2xl border border-white/15 bg-black/90 p-3 text-white shadow-xl backdrop-blur-xl motion-reduce:animate-none"
        >
          <p className="px-2 pb-2 text-xs font-medium text-white/65">Share this view</p>
          <button
            ref={copyButtonRef}
            type="button"
            onClick={handleCopy}
            disabled={pending}
            className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white disabled:opacity-60 motion-reduce:transition-none touch-manipulation"
          >
            {pending ? <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" /> : copied ? <Check aria-hidden="true" className="size-4 text-emerald-400" /> : <Copy aria-hidden="true" className="size-4" />}
            {copied ? "Link copied" : "Copy link"}
          </button>
          {nativeShareAvailable ? <button
            type="button"
            onClick={handleShare}
            disabled={pending}
            className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-sm text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-white disabled:opacity-60 motion-reduce:transition-none touch-manipulation"
          >
            <Share2 aria-hidden="true" className="size-4" />
            Share via device
          </button> : null}
          {manualFallback}
        </PopoverContent>
      </Popover>
    );
  }

  // Existing compact overlays retain their sizing; Launch opts into 44px targets.
  if (iconOnly) {
    return (
      <div className="relative flex shrink-0 items-center">
        <button
          ref={copyButtonRef}
          type="button"
          onClick={handleCopy}
          disabled={pending}
          aria-label={copied ? "Link copied" : "Copy link"}
          className={cn("inline-flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-white disabled:opacity-60 touch-manipulation", touchTarget ? "size-11" : "size-6")}
          title="Copy this page's URL to your clipboard"
        >
          {pending && !copied ? (
            <Loader2 className={cn("animate-spin motion-reduce:animate-none", touchTarget ? "size-4" : "size-3")} />
          ) : copied ? (
            <Check className={cn("text-emerald-400", touchTarget ? "size-4" : "size-3")} />
          ) : (
            <Copy className={touchTarget ? "size-4" : "size-3"} />
          )}
        </button>
        <button
          type="button"
          onClick={handleShare}
          disabled={pending}
          aria-label="Share"
          className={cn("inline-flex items-center justify-center rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-colors motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-white disabled:opacity-60 touch-manipulation", touchTarget ? "size-11" : "size-6")}
          title="Open the share menu (OS share sheet on mobile / supported desktops)"
        >
          <Share2 className={touchTarget ? "size-4" : "size-3"} />
        </button>
        {manualFallback}
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-end gap-2">
      <button
        ref={copyButtonRef}
        type="button"
        onClick={handleCopy}
        disabled={pending}
        // Tap target: WCAG/iOS HIG minimum 44px. We bump min-h to 11
        // (44px) on phones; sm+ mice can comfortably hit a 36px button
        // so we shrink back to min-h-9 there to keep the desktop
        // header chrome compact next to the larger title.
        className="inline-flex items-center gap-1.5 rounded-md border vs-border-soft bg-card px-3 py-2.5 sm:py-1.5 min-h-11 sm:min-h-9 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-60 touch-manipulation"
        title="Copy this page's URL to your clipboard"
      >
        {pending && !copied ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : copied ? (
          <Check className="h-3.5 w-3.5 text-emerald-500" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
        {copied ? "Copied" : "Copy link"}
      </button>
      {manualFallback}
      <button
        type="button"
        onClick={handleShare}
        disabled={pending}
        className="inline-flex items-center gap-1.5 rounded-md border vs-border-soft bg-card px-3 py-2.5 sm:py-1.5 min-h-11 sm:min-h-9 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-60 touch-manipulation"
        title="Open the share menu (OS share sheet on mobile / supported desktops)"
      >
        <Share2 className="h-3.5 w-3.5" />
        Share
      </button>
    </div>
  );
}
